import EnumValueProvider from "../../providers/EnumValueProvider.js";
import strikeZoneSector from "../../enums/StrikeZoneSector.js";

class StrikeZoneModelBase{
    constructor(){
        this.maxHorizontalOffset = 1000;
        this.maxVerticalOffset = 1000;
    }

    /**
     * returns top offset of the strike zone in meters from the home plate level
     */
    getStrikeZoneTop(){
        throw new Error('method getStrikeZoneTop is not implemented');
    }

    /**
     * returns bottom offset of the strike zone in meters from the home plate level
     */
    getStrikeZoneBottom(){
        throw new Error('method getStrikeZoneBottom is not implemented');
    }

    /**
     * returns left offset of the strike zone in meters from umpire's/catcher's perspective from the center of the home plate
     */
    getStrikeZoneLeft(){
        throw new Error('method getStrikeZoneLeft is not implemented');
    }

    /**
     * returns left right of the strike zone in meters from umpire's/catcher's perspective from the center of the home plate
     */
    getStrikeZoneRight(){
        throw new Error('method getStrikeZoneRight is not implemented');
    }

    /**
     * Allows to set max allowed offset for strike zone sectors 10-14 (out of the zone),
     * default 1000m Top, Bottom, Left and Right from the center of the  actual strike zone (catch them all sectors)
     * @param {*} horizontalOffset 
     * @param {*} verticalOffset 
     */
    setMaxAllowedOffset(horizontalOffset, verticalOffset){
        if (typeof(horizontalOffset) === 'number'){
            this.maxHorizontalOffset = horizontalOffset;
        }
        if (typeof(verticalOffset) === 'number'){
            this.maxVerticalOffset = verticalOffset;
        }

        return this;
    }

    /**
     * Allows to verify if pitch should be called strike or ball. 
     * @param {*} horizontalLocationM - horizontal location of the ball (from the center of the home plate) crossing home plate front plane, in meters from catcher's perspective.
     * @param {*} verticalLocationM - vertical location fo the ball (from the home plate level) crossing home plate front plane, in meters.
     * @returns true if pitched ball crossed the front plane of the home plane
     */
    isCalledStrike(horizontalLocationM, verticalLocationM){
        this.verifyLocation(horizontalLocationM, verticalLocationM);

        let isInZone = true;
        if (horizontalLocationM > this.getStrikeZoneRight()){
            isInZone = false;
        }
        else if(horizontalLocationM < this.getStrikeZoneLeft()){
            isInZone = false;
        }
        else if(verticalLocationM > this.getStrikeZoneTop()){
            isInZone = false;
        }
        else if(verticalLocationM < this.getStrikeZoneBottom()){
            isInZone = false;
        }

        return isInZone;
    }

    /**
     * Allows to check in which sector baseball crossed the strike zone plane
     * @param {*} horizontalLocationM - horizontal location of the ball (from the center of the home plate) crossing home plate front plane, in meters from catcher's perspective.
     * @param {*} verticalLocationM - vertical location fo the ball (from the home plate level) crossing home plate front plane, in meters.
     * @returns StrikeZoneSector enum key
     */
    determineSector(horizontalLocationM, verticalLocationM){
        this.verifyLocation(horizontalLocationM, verticalLocationM);

        const strikeZoneTop = this.getStrikeZoneTop();
        const strikeZoneBottom = this.getStrikeZoneBottom();
        const strikeZoneLeft = this.getStrikeZoneLeft();
        const strikeZoneRight = this.getStrikeZoneRight();

        // first check 9 symmetric sectors in the strike zone boundary
        const isInTheZone = this.isCalledStrike(horizontalLocationM, verticalLocationM);
        if (isInTheZone){
            let zoneNr = StrikeZoneModelBase.determineSectorOnGrid(
                strikeZoneTop, strikeZoneBottom, strikeZoneLeft, strikeZoneRight,
                3, 3,
                horizontalLocationM, verticalLocationM
            );

            if (zoneNr){
                return EnumValueProvider.getValue(zoneNr.toString(), strikeZoneSector);
            }

            throw new Error('Pitch has been classified as strike, but no sector inside strike zone has been matched');
        }

        // second step, check for called ball zones, which are catching all balls which did not cross strike zone boundary
        // perimeter of the strike zone has been increased by 20m, which will guarantee symmetric increase ability to catch almost all balls
        let zoneNr = StrikeZoneModelBase.determineSectorOnGrid(
            strikeZoneTop + this.maxVerticalOffset,
            strikeZoneBottom - this.maxVerticalOffset,
            strikeZoneLeft - this.maxHorizontalOffset,
            strikeZoneRight + this.maxHorizontalOffset,
            2, 2,
            horizontalLocationM, verticalLocationM
        );

        if (zoneNr){
            return EnumValueProvider.getValue((zoneNr + 10).toString(), strikeZoneSector);
        }

        throw new Error('Pitch has been classified as ball, but no sector outside of the strike zone has been matched');
    }

    /**
     * Allows to check in which attack region baseball crossed the strike zone plane
     * @param {*} horizontalLocationM - horizontal location of the ball (from the center of the home plate) crossing home plate front plane, in meters from catcher's perspective.
     * @param {*} verticalLocationM - vertical location fo the ball (from the home plate level) crossing home plate front plane, in meters.
     * @returns AttackRegion enum key
     */
    determineRegion(horizontalLocationM, verticalLocationM){
        // todo
        return;
    }

    verifyLocation(horizontalLocationM, verticalLocationM){
        if (typeof(horizontalLocationM) !== 'number'){
            throw new TypeError('horizontalLocationM must contain number');
        }
        if (typeof(verticalLocationM) !== 'number'){
            throw new TypeError('verticalLocationM must contain number');
        }

        return true;
    }

    static determineSectorOnGrid(rectangleTop, rectangleBottom, rectangleLeft, rectangleRight, rows, cols, pointX, pointY){
        const nrOfSectors = rows * cols;
        const rectangleHeight = rectangleTop - rectangleBottom;
        const rectangleWidth = rectangleRight - rectangleLeft;
        const roundingPrecision = Math.pow(10, 4);

        for (let i = 0; i < nrOfSectors; i++){
            // from the top left...
            const rowNr = Math.floor(i/rows) % rows;

            const sectorTop = rectangleTop - rectangleHeight * (rowNr / rows);
            const sectorBottom = rectangleBottom + rectangleHeight * ((rows - 1 - rowNr) / rows);
            const sectorTopRounded = Math.round(sectorTop * roundingPrecision) / roundingPrecision;
            const sectorBottomRounded = Math.round(sectorBottom * roundingPrecision) / roundingPrecision;

            if (pointY > sectorTopRounded){
                // we can safely continue to next row here
                i += cols - 1;
                continue;
            }
            else if(pointY < sectorBottomRounded){
                 // we can safely continue to next row here
                 i += cols - 1;
                continue;
            }

            const colNr = i % cols;
            const sectorLeft = rectangleLeft + rectangleWidth * (colNr / cols);
            const sectorRight = rectangleRight - rectangleWidth * (( cols - 1 - colNr) / cols);

            const sectorLeftRounded = Math.round(sectorLeft * roundingPrecision) / roundingPrecision;
            const sectorRightRounded = Math.round(sectorRight * roundingPrecision) / roundingPrecision;

            if (pointX > sectorRightRounded){
                continue;
            }
            else if(pointX < sectorLeftRounded){
                continue;
            }

            return i + 1;
        }

        return 0;
    }
}

export default StrikeZoneModelBase;